// react
import { useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// material ui
import { Box, Chip, Divider, Skeleton, Stack } from '@mui/material';

// relative
import useHighchartsThemeSync from '../../../../../../../hooks/useHighchartsThemeSync';
import type { PortfolioAnalysisBenchmarkChartProps } from './benchmark';
import BenchmarkAssessment from './BenchmarkAssessment';
import BenchmarkBuildingInfo from './BenchmarkBuildingInfo';
import BenchmarkKpiCards from './BenchmarkKpiCards';
import useBenchmarkChartOptions from './useBenchmarkChartOptions';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const PortfolioAnalysisBenchmarkChart = ({
  address,
  data = {},
  isLoading,
}: PortfolioAnalysisBenchmarkChartProps) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject | null>(null);
  const { t } = useTranslation('portfolio-analysis');
  const { options } = useBenchmarkChartOptions(data);
  useHighchartsThemeSync(chartComponentRef, isLoading);

  const benchmarkData = data.BenchmarkData;
  const totalBuildings = (data.BenchmarkHistogram ?? []).reduce(
    (sum, bin) => sum + bin.Count,
    0,
  );

  return (
    <Stack spacing={2}>
      {benchmarkData ? (
        <>
          <BenchmarkBuildingInfo benchmarkData={benchmarkData} />
          <BenchmarkKpiCards benchmarkData={benchmarkData} />
        </>
      ) : (
        address && (
          <Chip
            icon={<InfoOutlinedIcon fontSize="small" />}
            label={address}
            variant="outlined"
            size="small"
          />
        )
      )}

      <Box sx={{ width: '100%' }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartComponentRef}
        />
      </Box>

      {(benchmarkData || isLoading) && (
        <>
          <Divider />
          {benchmarkData && (
            <BenchmarkAssessment
              benchmarkData={benchmarkData}
              benchmarkPDF={data.BenchmarkPDF ?? []}
              isLoading={isLoading}
            />
          )}
          {isLoading && !benchmarkData && (
            <Stack spacing={1}>
              <Skeleton
                variant="rounded"
                height={80}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="rounded"
                height={64}
                sx={{ borderRadius: 2 }}
              />
            </Stack>
          )}
        </>
      )}

      {benchmarkData && (
        <Chip
          icon={<InfoOutlinedIcon fontSize="small" />}
          label={t('benchmarkChart.chart.basedOn', {
            count: totalBuildings,
          })}
          variant="outlined"
          size="small"
        />
      )}
    </Stack>
  );
};

export default PortfolioAnalysisBenchmarkChart;
